import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Settings, Save, RotateCcw, AlertTriangle, Info, Check, X, Edit2 } from 'lucide-react';
import api from '../services/api';
import { useToast, LoadingSkeleton, Tooltip } from '../components/ui';

export default function SystemSettings() {
  const { success, error: showError } = useToast();
  const queryClient = useQueryClient();

  const [selectedCategory, setSelectedCategory] = useState('all');
  const [editingSettings, setEditingSettings] = useState({});
  const [editValues, setEditValues] = useState({});

  // Fetch system settings
  const { data: settings, isLoading } = useQuery({
    queryKey: ['system-settings'],
    queryFn: async () => {
      const response = await api.get('/management/settings/');
      return response.data.results || response.data;
    }
  });

  // Update setting mutation
  const updateSettingMutation = useMutation({
    mutationFn: async ({ settingId, value }) => {
      const response = await api.patch(`/management/settings/${settingId}/`, { value });
      return response.data;
    },
    onSuccess: (data) => {
      success('Setting updated successfully');
      queryClient.invalidateQueries(['system-settings']);
      // Clear editing state
      setEditingSettings(prev => ({ ...prev, [data.id]: false }));
      setEditValues(prev => {
        const newValues = { ...prev };
        delete newValues[data.id];
        return newValues;
      });
    },
    onError: (err) => {
      showError(err.response?.data?.error || err.response?.data?.value?.[0] || 'Failed to update setting');
    }
  });

  // Reset setting to default
  const resetSettingMutation = useMutation({
    mutationFn: async (settingId) => {
      const response = await api.post(`/management/settings/${settingId}/reset/`);
      return response.data;
    },
    onSuccess: () => {
      success('Setting reset to default value');
      queryClient.invalidateQueries(['system-settings']);
    },
    onError: (err) => {
      showError(err.response?.data?.error || 'Failed to reset setting');
    }
  });

  // Categories for filtering
  const categories = [
    { value: 'all', label: 'All Settings', count: settings?.length || 0 },
    { value: 'DOCUMENT_VALIDITY', label: 'Document Validity', count: settings?.filter(s => s.category === 'DOCUMENT_VALIDITY').length || 0 },
    { value: 'QC_INSPECTION', label: 'QC Inspection', count: settings?.filter(s => s.category === 'QC_INSPECTION').length || 0 },
    { value: 'GATE_ENTRY', label: 'Gate Entry', count: settings?.filter(s => s.category === 'GATE_ENTRY').length || 0 },
    { value: 'GRN', label: 'GRN', count: settings?.filter(s => s.category === 'GRN').length || 0 },
    { value: 'WAREHOUSE', label: 'Warehouse', count: settings?.filter(s => s.category === 'WAREHOUSE').length || 0 },
    { value: 'DOCK_MANAGEMENT', label: 'Dock Management', count: settings?.filter(s => s.category === 'DOCK_MANAGEMENT').length || 0 },
    { value: 'SAP_INTEGRATION', label: 'SAP Integration', count: settings?.filter(s => s.category === 'SAP_INTEGRATION').length || 0 },
    { value: 'WORKFLOW', label: 'Workflow', count: settings?.filter(s => s.category === 'WORKFLOW').length || 0 },
    { value: 'NOTIFICATIONS', label: 'Notifications', count: settings?.filter(s => s.category === 'NOTIFICATIONS').length || 0 }
  ];

  // Filter settings by category
  const filteredSettings = selectedCategory === 'all'
    ? settings
    : settings?.filter(s => s.category === selectedCategory);

  // Handle edit start
  const handleEditClick = (setting) => {
    setEditingSettings(prev => ({ ...prev, [setting.id]: true }));
    setEditValues(prev => ({ ...prev, [setting.id]: setting.value }));
  };

  // Handle edit cancel
  const handleCancelEdit = (settingId) => {
    setEditingSettings(prev => ({ ...prev, [settingId]: false }));
    setEditValues(prev => {
      const newValues = { ...prev };
      delete newValues[settingId];
      return newValues;
    });
  };

  // Handle save
  const handleSave = (setting) => {
    const newValue = editValues[setting.id];
    if (newValue === undefined || newValue === setting.value) {
      handleCancelEdit(setting.id);
      return;
    }
    updateSettingMutation.mutate({ settingId: setting.id, value: newValue });
  };

  // Handle reset
  const handleReset = (settingId) => {
    if (window.confirm('Are you sure you want to reset this setting to its default value?')) {
      resetSettingMutation.mutate(settingId);
    }
  };

  // Render input field based on data type
  const renderInput = (setting) => {
    const value = editValues[setting.id] ?? setting.value;
    const isEditing = editingSettings[setting.id];

    if (!isEditing) {
      // Display mode
      if (setting.data_type === 'boolean') {
        return (
          <span className={`px-2 py-1 rounded text-sm font-medium ${
            setting.typed_value ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
          }`}>
            {setting.typed_value ? 'Enabled' : 'Disabled'}
          </span>
        );
      }
      return <span className="text-gray-900 font-medium">{setting.value}</span>;
    }

    // Edit mode
    if (setting.data_type === 'boolean') {
      return (
        <select
          value={value}
          onChange={(e) => setEditValues(prev => ({ ...prev, [setting.id]: e.target.value }))}
          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
        >
          <option value="true">Enabled</option>
          <option value="false">Disabled</option>
        </select>
      );
    } else if (setting.allowed_values && setting.allowed_values.length > 0) {
      return (
        <select
          value={value}
          onChange={(e) => setEditValues(prev => ({ ...prev, [setting.id]: e.target.value }))}
          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
        >
          {setting.allowed_values.map(val => (
            <option key={val} value={val}>{val}</option>
          ))}
        </select>
      );
    } else if (setting.data_type === 'integer' || setting.data_type === 'decimal') {
      return (
        <div className="flex items-center gap-2">
          <input
            type="number"
            value={value}
            onChange={(e) => setEditValues(prev => ({ ...prev, [setting.id]: e.target.value }))}
            min={setting.min_value}
            max={setting.max_value}
            step={setting.data_type === 'decimal' ? '0.01' : '1'}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 w-32"
          />
          {(setting.min_value !== null || setting.max_value !== null) && (
            <span className="text-xs text-gray-500">
              {setting.min_value !== null && `Min: ${setting.min_value}`}
              {setting.min_value !== null && setting.max_value !== null && ' | '}
              {setting.max_value !== null && `Max: ${setting.max_value}`}
            </span>
          )}
        </div>
      );
    } else {
      return (
        <input
          type="text"
          value={value}
          onChange={(e) => setEditValues(prev => ({ ...prev, [setting.id]: e.target.value }))}
          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 w-full max-w-md"
        />
      );
    }
  };

  if (isLoading) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="mb-6">
          <div className="flex items-center mb-2">
            <Settings className="w-8 h-8 text-blue-600 mr-3" />
            <h1 className="text-2xl font-bold text-gray-900">System Settings</h1>
          </div>
          <p className="text-gray-600">Configure system-wide settings and parameters</p>
        </div>
        <LoadingSkeleton type="list" count={3} />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center mb-2">
          <Settings className="w-8 h-8 text-blue-600 mr-3" />
          <h1 className="text-2xl font-bold text-gray-900">System Settings</h1>
          <Tooltip content="System-wide configuration parameters that control application behavior. Changes may require restart." position="right" />
        </div>
        <p className="text-gray-600">Configure system-wide settings and parameters</p>
      </div>

      {/* Warning Banner */}
      <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex items-start">
          <AlertTriangle className="w-5 h-5 text-yellow-600 mr-3 mt-0.5" />
          <div>
            <p className="font-semibold text-yellow-900">Important</p>
            <p className="text-sm text-yellow-800">
              Changes to these settings affect the entire system. Some settings may require a system restart to take effect.
              Always review the description before modifying a setting.
            </p>
          </div>
        </div>
      </div>

      {/* Category Filters */}
      <div className="mb-6 bg-white rounded-lg shadow p-4">
        <div className="flex flex-wrap gap-2">
          {categories.map((cat) => (
            <button
              key={cat.value}
              onClick={() => setSelectedCategory(cat.value)}
              className={`px-4 py-2 rounded-lg transition-colors ${
                selectedCategory === cat.value
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {cat.label}
              <span className="ml-2 text-xs opacity-75">({cat.count})</span>
            </button>
          ))}
        </div>
      </div>

      {/* Settings List */}
      <div className="space-y-4">
        {filteredSettings && filteredSettings.length > 0 ? (
          filteredSettings.map((setting) => (
            <div key={setting.id} className="bg-white rounded-lg shadow p-5">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {setting.display_name}
                    </h3>
                    <span className="px-2 py-0.5 bg-gray-100 text-gray-700 text-xs rounded">
                      {setting.category_display}
                    </span>
                    {setting.requires_restart && (
                      <span className="px-2 py-0.5 bg-orange-100 text-orange-700 text-xs rounded flex items-center">
                        <AlertTriangle className="w-3 h-3 mr-1" />
                        Requires Restart
                      </span>
                    )}
                  </div>

                  <p className="text-sm text-gray-600 mb-3">{setting.description}</p>

                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-500">Current Value:</span>
                      {renderInput(setting)}
                    </div>

                    {setting.value !== setting.default_value && (
                      <div className="flex items-center gap-2 text-sm">
                        <Info className="w-4 h-4 text-blue-500" />
                        <span className="text-gray-500">
                          Default: <span className="font-medium text-gray-700">{setting.default_value}</span>
                        </span>
                      </div>
                    )}
                  </div>

                  {setting.updated_by_name && (
                    <p className="text-xs text-gray-500 mt-2">
                      Last updated by {setting.updated_by_name} on {new Date(setting.updated_at).toLocaleString()}
                    </p>
                  )}
                </div>

                {setting.is_editable && (
                  <div className="ml-4 flex items-center gap-2">
                    {editingSettings[setting.id] ? (
                      <>
                        <button
                          onClick={() => handleSave(setting)}
                          disabled={updateSettingMutation.isLoading}
                          className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                          title="Save changes"
                        >
                          <Check className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleCancelEdit(setting.id)}
                          disabled={updateSettingMutation.isLoading}
                          className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                          title="Cancel"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => handleEditClick(setting)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Edit setting"
                        >
                          <Edit2 className="w-5 h-5" />
                        </button>
                        {setting.value !== setting.default_value && (
                          <button
                            onClick={() => handleReset(setting.id)}
                            disabled={resetSettingMutation.isLoading}
                            className="p-2 text-orange-600 hover:bg-orange-50 rounded-lg transition-colors"
                            title="Reset to default"
                          >
                            <RotateCcw className="w-5 h-5" />
                          </button>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))
        ) : (
          <div className="bg-white rounded-lg shadow p-8 text-center">
            <p className="text-gray-500">No settings found for this category</p>
          </div>
        )}
      </div>
    </div>
  );
}
